import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, Route, Mountain } from "lucide-react";
import api from "../lib/api";
import { resolveImg } from "../lib/img";
import BaladeMap from "../components/BaladeMap";
import SEO, { breadcrumbLD, SITE_URL } from "../components/SEO";

// Markdown-lite renderer — supports:
//   **bold** · *italic*
//   lines starting with "- " or "1. " become list items
//   lines starting with "**Title**" in their own block become a heading
function renderInline(text) {
    const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
    return parts.map((s, i) => {
        if (s.startsWith("**") && s.endsWith("**")) {
            return <strong key={i} className="font-normal text-cp-foret">{s.slice(2, -2)}</strong>;
        }
        if (s.startsWith("*") && s.endsWith("*") && s.length > 2) {
            return <em key={i} className="cp-display-italic">{s.slice(1, -1)}</em>;
        }
        return <span key={i}>{s}</span>;
    });
}

function renderDescription(text) {
    const blocks = (text || "").split(/\n\s*\n/);
    return blocks.map((b, idx) => {
        const t = b.trim();
        const lines = t.split("\n");
        // numbered list (1. 2. 3.)
        if (lines.every((l) => /^\d+\.\s+/.test(l))) {
            return (
                <ol key={idx} className="space-y-3 my-6 list-decimal list-inside text-cp-noir text-lg">
                    {lines.map((l, i) => (
                        <li key={i} className="pl-2">{renderInline(l.replace(/^\d+\.\s+/, ""))}</li>
                    ))}
                </ol>
            );
        }
        // bullet list
        if (lines.every((l) => /^-\s+/.test(l))) {
            return (
                <ul key={idx} className="space-y-3 my-6 list-disc list-inside text-cp-noir text-lg">
                    {lines.map((l, i) => (
                        <li key={i} className="pl-2">{renderInline(l.replace(/^-\s+/, ""))}</li>
                    ))}
                </ul>
            );
        }
        // full bold line = subheading
        if (/^\*\*[^*]+\*\*$/.test(t)) {
            return (
                <h3 key={idx} className="cp-serif italic text-2xl text-cp-foret mt-10 mb-4 cp-display-italic">
                    {t.replace(/^\*\*|\*\*$/g, "")}
                </h3>
            );
        }
        return (
            <p key={idx} className="text-cp-noir text-lg md:text-xl font-light leading-[1.7] mb-6 whitespace-pre-line">
                {renderInline(t)}
            </p>
        );
    });
}

export default function BaladeDetail() {
    const { slug } = useParams();
    const [walk, setWalk] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        setWalk(null);
        setError("");
        api.get(`/walks/${slug}`).then((r) => setWalk(r.data)).catch(() => setError("Balade introuvable."));
    }, [slug]);

    if (error) return <div className="pt-40 text-center pb-40"><h1 className="cp-serif text-5xl">{error}</h1><Link to="/balades" className="cp-btn-ghost mt-10 inline-flex">Retour</Link></div>;
    if (!walk) return <div className="pt-40 text-center text-cp-gris">Chargement…</div>;

    const heroImg = walk.hero_image || walk.image;
    const tourismLD = {
        "@context": "https://schema.org",
        "@type": "TouristAttraction",
        "name": walk.title,
        "description": (walk.description || "").slice(0, 320),
        "image": heroImg ? (heroImg.startsWith("http") ? heroImg : `${SITE_URL}${heroImg}`) : undefined,
        "touristType": ["Hiker", "Nature lover"],
        "isAccessibleForFree": true,
        ...(walk.start_lat && walk.start_lng ? {
            "geo": { "@type": "GeoCoordinates", "latitude": walk.start_lat, "longitude": walk.start_lng },
        } : {}),
        "address": {
            "@type": "PostalAddress",
            "addressRegion": "Normandie / Picardie",
            "addressCountry": "FR",
            "addressLocality": walk.city_slug || "",
        },
    };

    return (
        <div data-testid={`walk-detail-${slug}`}>
            <SEO
                title={`${walk.title} · randonnée balisée`}
                description={`${walk.title} — randonnée ${walk.distance_km} km · ${walk.duration_minutes} min · ${walk.difficulty}. Trace GPX, parcours balisé entre Normandie et Picardie.`}
                image={heroImg}
                path={`/balades/${slug}`}
                type="article"
                keywords={[
                    walk.title, "randonnée", "Normandie", "Picardie",
                    "Forêt d'Eu", "balade GPX", walk.theme || "",
                    ...(walk.tags || []),
                ]}
                structuredData={[
                    tourismLD,
                    breadcrumbLD([
                        { name: "Accueil", path: "/" },
                        { name: "Balades", path: "/balades" },
                        { name: walk.title, path: `/balades/${slug}` },
                    ]),
                ]}
            />
            <section className="relative min-h-[70vh] overflow-hidden">
                <img src={resolveImg(heroImg)} alt={walk.title} className="absolute inset-0 w-full h-full object-cover cp-kenburns cp-photo-real-dark" />
                <div className="absolute inset-0 cp-hero-overlay" />
                <div className="relative z-10 flex flex-col justify-end min-h-[70vh] px-6 md:px-12 py-20 max-w-[1600px] mx-auto">
                    <Link to="/balades" className="cp-kicker text-cp-ivoire/70 hover:text-cp-ivoire flex items-center gap-2 mb-6">
                        <ArrowLeft size={14} strokeWidth={1.5} /> Balades
                    </Link>
                    <p className="cp-kicker text-cp-sable mb-4">{walk.theme} · {walk.difficulty}</p>
                    <h1 className="cp-serif text-cp-ivoire text-3xl sm:text-4xl md:text-5xl lg:text-[56px] xl:text-[72px] leading-[1.05] max-w-4xl">
                        {walk.title}
                    </h1>
                </div>
            </section>

            <section className="px-6 md:px-12 max-w-[1400px] mx-auto py-24 grid grid-cols-1 md:grid-cols-12 gap-10">
                <aside className="md:col-span-4 flex flex-col gap-6">
                    <div>
                        <p className="cp-kicker text-cp-foret mb-2">Distance</p>
                        <p className="flex items-center gap-2 text-cp-noir text-xl"><Route size={18} strokeWidth={1.5} /> {walk.distance_km} km</p>
                    </div>
                    <div>
                        <p className="cp-kicker text-cp-foret mb-2">Durée</p>
                        <p className="flex items-center gap-2 text-cp-noir text-xl"><Clock size={18} strokeWidth={1.5} /> {walk.duration_minutes} min</p>
                    </div>
                    <div>
                        <p className="cp-kicker text-cp-foret mb-2">Niveau</p>
                        <p className="flex items-center gap-2 text-cp-noir text-xl"><Mountain size={18} strokeWidth={1.5} /> {walk.difficulty}</p>
                    </div>
                    {walk.tags?.length > 0 && (
                        <div>
                            <p className="cp-kicker text-cp-foret mb-3">Ambiance</p>
                            <div className="flex flex-wrap gap-2">
                                {walk.tags.map((t, i) => (
                                    <span key={i} className="cp-tiny px-3 py-1 border border-cp-border text-cp-gris">{t}</span>
                                ))}
                            </div>
                        </div>
                    )}
                </aside>
                <div className="md:col-span-8" data-testid="walk-description">
                    {renderDescription(walk.description)}
                </div>
            </section>

            <section className="px-6 md:px-12 max-w-[1400px] mx-auto pb-28">
                <div className="flex items-end justify-between mb-6">
                    <div>
                        <p className="cp-kicker text-cp-foret mb-2">Parcours balisé</p>
                        <h2 className="cp-serif italic text-3xl text-cp-noir">Le tracé sur la carte</h2>
                    </div>
                    {walk.waypoints?.length >= 2 && (
                        <p className="cp-tiny text-cp-gris">{walk.waypoints.length} points · fond OpenStreetMap</p>
                    )}
                </div>
                <BaladeMap
                    waypoints={walk.waypoints}
                    startLat={walk.start_lat}
                    startLng={walk.start_lng}
                    title={walk.title}
                />
                {(!walk.waypoints || walk.waypoints.length < 2) && (
                    <p className="cp-tiny text-cp-gris mt-3 italic">Le tracé complet sera publié prochainement — point de départ indiqué.</p>
                )}
            </section>
        </div>
    );
}
